import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { FEATURE_FLAGS, defaultValue, type FeatureFlagKey } from "./flags";

// ─── Architecture ────────────────────────────────────────────────────────────
//
// Quatre sources avec priorité décroissante :
//   1. LOCAL OVERRIDE  — l'utilisateur (ou un dev) force une valeur en runtime
//                        via setOverride(). Persisté en localStorage.
//                        Utile pour les QA / debug. Jamais en prod.
//   2. REMOTE          — valeur servie par un backend de feature flags (Growth-
//                        Book, LaunchDarkly, ou un endpoint custom du Gateway).
//                        Hydraté au boot, refresh périodique possible.
//   3. BUILD CONFIG    — valeur statique dans `VITE_FEATURE_FLAGS` (JSON),
//                        appliquée au déploiement.
//   4. DEFAULT         — valeur déclarée dans flags.ts (compile-time).
//
// Le module n'embarque AUCUN SDK tiers : il expose une interface pour brancher
// celui de votre choix. Pour intégrer GrowthBook :
//   1. `npm install @growthbook/growthbook-react`
//   2. Remplacer `fetchRemoteFlags()` par `growthbook.evalFeatures()`
//   3. Subscriber aux changes pour refresh sans rechargement.
//
// Port web : localStorage remplace AsyncStorage (l'API devient synchrone, mais
// on garde les signatures asynchrones pour ne pas changer le contrat côté
// écrans), et `VITE_FEATURE_FLAGS` remplace `app.json.extra.featureFlags`.

// ─── Sources ──────────────────────────────────────────────────────────────────

const OVERRIDE_STORAGE_KEY = "@feature_flags_override";

type FlagValue = boolean;
type FlagBag = Partial<Record<FeatureFlagKey, FlagValue>>;

function readBuildConfigFlags(): FlagBag {
  const raw = import.meta.env.VITE_FEATURE_FLAGS;
  if (!raw) return {};
  try {
    return JSON.parse(raw) as FlagBag;
  } catch {
    // JSON malformé : on ignore plutôt que de casser le boot.
    console.warn("[flags] VITE_FEATURE_FLAGS ignoré — JSON invalide");
    return {};
  }
}

async function readOverrides(): Promise<FlagBag> {
  try {
    const raw = localStorage.getItem(OVERRIDE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FlagBag) : {};
  } catch {
    return {};
  }
}

async function writeOverrides(bag: FlagBag): Promise<void> {
  try {
    localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(bag));
  } catch {
    /* silencieux — best effort */
  }
}

// Stub remote — à brancher sur GrowthBook/LaunchDarkly/endpoint custom.
// Pour l'instant, on retourne un bag vide. Si le backend Alomain expose un
// endpoint `/feature-flags` plus tard, c'est ici qu'on l'appelle.
async function fetchRemoteFlags(): Promise<FlagBag> {
  // Exemple intégration GrowthBook :
  //   const gb = new GrowthBook({ apiHost, clientKey, attributes: { userId, role } });
  //   await gb.loadFeatures();
  //   return Object.fromEntries(
  //     (Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]).map(k => [k, gb.isOn(k)])
  //   );
  return {};
}

// ─── Contexte React ──────────────────────────────────────────────────────────

type FeatureFlagContextValue = {
  flags: Record<FeatureFlagKey, boolean>;
  setOverride: (key: FeatureFlagKey, value: boolean | null) => Promise<void>;
  refresh: () => Promise<void>;
  isHydrated: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

function resolveFlags(
  overrides: FlagBag,
  remote: FlagBag,
  buildConfig: FlagBag,
): Record<FeatureFlagKey, boolean> {
  const keys = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
  const out = {} as Record<FeatureFlagKey, boolean>;
  for (const k of keys) {
    out[k] = overrides[k] ?? remote[k] ?? buildConfig[k] ?? defaultValue(k);
  }
  return out;
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverridesState] = useState<FlagBag>({});
  const [remote, setRemote] = useState<FlagBag>({});
  const [isHydrated, setIsHydrated] = useState(false);
  const buildConfig = useMemo(() => readBuildConfigFlags(), []);

  // Hydratation initiale : overrides locaux + remote (réseau).
  // On lance les deux en parallèle pour minimiser le délai d'init.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [ov, rem] = await Promise.all([readOverrides(), fetchRemoteFlags()]);
      if (cancelled) return;
      setOverridesState(ov);
      setRemote(rem);
      setIsHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOverride = useCallback(async (key: FeatureFlagKey, value: boolean | null) => {
    setOverridesState((prev) => {
      const next = { ...prev };
      if (value === null) delete next[key];
      else next[key] = value;
      void writeOverrides(next);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    const rem = await fetchRemoteFlags();
    setRemote(rem);
  }, []);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      flags: resolveFlags(overrides, remote, buildConfig),
      setOverride,
      refresh,
      isHydrated,
    }),
    [overrides, remote, buildConfig, setOverride, refresh, isHydrated],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

// ─── Hooks consommateurs ──────────────────────────────────────────────────────

/**
 * Lit la valeur courante d'un flag. Re-render automatique quand le flag change
 * (set via setOverride ou refresh remote).
 *
 * Usage :
 *   const showVip = useFeatureFlag("vipTeaser");
 *   {showVip && <VipTeaserCard />}
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    // Si le provider n'est pas monté (test, story…), on retombe sur la valeur
    // par défaut compile-time — meilleur que crash.
    return defaultValue(key);
  }
  return ctx.flags[key];
}

/**
 * Accède à toutes les actions de feature flag (refresh, override).
 * À utiliser dans les écrans de dev tools, jamais dans la logique métier.
 */
export function useFeatureFlagsContext(): FeatureFlagContextValue {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error("useFeatureFlagsContext() doit être appelé sous un <FeatureFlagProvider>.");
  }
  return ctx;
}
